import AddSharpIcon from "@mui/icons-material/AddSharp";
import AutoFixHighSharpIcon from "@mui/icons-material/AutoFixHighSharp";
import InfoIcon from "@mui/icons-material/Info";
import RemoveSharpIcon from "@mui/icons-material/RemoveSharp";
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Validator } from "jsonschema";
import { forwardRef, useContext, useEffect, useState } from "react";
import { ConstantContext } from "components/context/ConstantProvider";
import { SnackbarContext } from "components/context/SnackbarProvider";
import WithConfirmationButton from "components/ui/atoms/WithConfirmationButton";
import WithValueModalComponent from "components/ui/atoms/WithValueModalComponent";

const validationSchema = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  $id: "https://ringcp.com/components/ui/atoms/formFields/FinancialsData",
  title: "FinancialsData",
  description: "Financials Data",
  type: "object",
  properties: {
    currency_code: {
      description:
        "The currency code (https://fr.wikipedia.org/wiki/ISO_4217#Index_par_code)",
      type: "string",
    },
    lastChangeDate: {
      description: "Last change date",
      type: "string",
    },
    years: {
      description: "years",
      type: "array",
      items: {
        type: "object",
        properties: {
          year: {
            type: "integer",
          },
          revenue: {
            type: "float",
          },
          grossProfit: {
            type: "float",
          },
          ebitda: {
            type: "float",
          },
        },
        required: ["year"],
      },
    },
  },
  required: ["years", "currency_code"],
};
const validator = new Validator();

// should be serializable
const rowsDefinitions = [
  { Label: "Revenue", id: "revenue" },
  {
    Label: "Growth rate",
    id: "growthRate",
  },
  {
    Label: "Gross profit",
    id: "grossProfit",
  },
  {
    Label: "as a % of revenue",
    id: "percentGrossProfit",
  },
  { Label: "Ebitda", id: "ebitda" },
  {
    Label: "as a % of revenue",
    id: "percentEbitda",
  },
];
const firstColumn = {
  field: "Label",
  headerName: "",
  sortable: false,
  editable: false,
  width: 160,
};
const getRowHeight = ({ densityFactor, model }) => {
  const rowHeightFactors = {
    growthRate: 0.7,
    percentGrossProfit: 0.7,
    percentEbitda: 0.7,
  };
  return densityFactor * (rowHeightFactors[model.id] || 1) * 50;
};
const getRowClassName = ({ row }) => {
  const rowClassNames = {
    growthRate: "derivated-row",
    percentGrossProfit: "derivated-row",
    percentEbitda: "derivated-row",
  };
  return rowClassNames[row.id];
};
const isCellEditable = ({ row }) => {
  const readOnlys = {
    growthRate: true,
    percentGrossProfit: true,
    percentEbitda: true,
  };
  return !readOnlys[row.id];
};
const calculateGrowthRate = (current, previous) => {
  if (
    typeof current === "number" &&
    typeof previous === "number" &&
    previous !== 0
  ) {
    return ((current - previous) / previous) * 100;
  } else {
    return NaN;
  }
};
const calcutatePercentOf = (source, base) => {
  if (typeof source === "number" && typeof base === "number" && base !== 0) {
    return (source / base) * 100;
  } else {
    return NaN;
  }
};
const recalcGrowthRate = (state, sourceRowId, destinationRowId) => {
  const sourceRow = state.rows.find((r) => r.id == sourceRowId);
  const destinationRow = state.rows.find((r) => r.id == destinationRowId);
  state.columns.forEach((colDef) => {
    if (colDef.field == firstColumn.field) return;
    const year = parseInt(colDef.headerName);
    if (year == NaN) {
      destinationRow[colDef.field] = NaN;
      return;
    }
    const previousColumn = state.columns.find(
      (col) => parseInt(col.headerName) == year - 1,
    );
    if (!previousColumn) {
      destinationRow[colDef.field] = NaN;
      return;
    }
    destinationRow[colDef.field] = calculateGrowthRate(
      sourceRow[colDef.field],
      sourceRow[previousColumn.field],
    );
  });
};
const recalcPercentage = (state, sourceRowId, baseRowId, destinationRowId) => {
  const sourceRow = state.rows.find((r) => r.id == sourceRowId);
  const baseRow = state.rows.find((r) => r.id == baseRowId);
  const destinationRow = state.rows.find((r) => r.id == destinationRowId);
  state.columns.forEach((colDef) => {
    if (colDef.field == firstColumn.field) return;
    destinationRow[colDef.field] = calcutatePercentOf(
      sourceRow[colDef.field],
      baseRow[colDef.field],
    );
  });
};
const recalcCalculatedValues = (state) => {
  const new_state = { ...state };
  recalcGrowthRate(new_state, "revenue", "growthRate");
  recalcPercentage(new_state, "grossProfit", "revenue", "percentGrossProfit");
  recalcPercentage(new_state, "ebitda", "revenue", "percentEbitda");
  return new_state;
};
const CustomDeleteColumnItem = ({ colDef, onEvent, hideMenu }) => {
  const onClick = () => {
    onEvent("deleteColumn", { year: colDef.field });
    hideMenu();
  };
  return (
    <WithConfirmationButton
      Component={MenuItem}
      onClick={onClick}
      confirmationMessage="Are your sure ?"
    >
      <ListItemIcon>
        <RemoveSharpIcon sx={{ scale: "0.8" }} />
      </ListItemIcon>
      <ListItemText>Delete</ListItemText>
    </WithConfirmationButton>
  );
};
const CustomAddColumnItem = ({ value, onEvent, hideMenu }) => {
  const getInitialValue = () => {
    return (
      value.years.reduce((t, v) => {
        if (isNaN(v.year)) return t;
        if (isNaN(t)) return v.year;
        return Math.max(t, v.year);
      }, null) || new Date().getFullYear() - 1
    );
  };

  const validator = (newYear) => {
    if (value.years.find((x) => x.year == parseInt(newYear)))
      throw Error(`The year '${newYear}' already exists.`);
    const newYearInt = parseInt(newYear);
    if (isNaN(newYearInt))
      throw Error(`The year '${newYear}' is not a number.`);
    if (newYearInt < 1950 || newYearInt > 2050)
      throw Error(
        `The year '${newYear}' is not in the right range [1950-2050]. In case you have only a BP with year-1, year-2, ... Make it start with the more realistic between current year or next year.`,
      );
  };

  const onClick = (newYear, oldYear) => {
    onEvent("addColumn", { newYear: newYear, oldYear: oldYear });
    hideMenu();
  };

  return (
    <WithValueModalComponent
      Component={MenuItem}
      initialValue={getInitialValue() + 1}
      validator={validator}
      onClick={onClick}
      onCancel={hideMenu}
      endIcon={<AddSharpIcon sx={{ scale: "0.8" }} />}
      title={"Add year"}
      confirmationButtonLabel={"Add"}
    >
      <ListItemIcon>
        <AddSharpIcon sx={{ scale: "0.8" }} />
      </ListItemIcon>
      <ListItemText>Add</ListItemText>
    </WithValueModalComponent>
  );
};
const CustomModifyColumnItem = ({ value, onEvent, colDef, hideMenu }) => {
  const validator = (newYear) => {
    if (value.years.find((x) => x.year == parseInt(newYear)))
      throw new Error(`The year '${newYear}' already exists.`);
    const newYearInt = parseInt(newYear);
    if (isNaN(newYearInt))
      throw new Error(`The year '${newYear}' is not a number.`);
    if (newYearInt < 1950 || newYearInt > 2050)
      throw new Error(
        `The year '${newYear}' is not in the right range [1950-2050]. In case you have only a BP with year-1, year-2, ... Make it start with the more realistic between current year or next year.`,
      );
  };

  const onClick = (newYear, oldYear) => {
    onEvent("modifyColumn", { newYear: newYear, oldYear: oldYear });
    hideMenu();
  };

  return (
    <WithValueModalComponent
      Component={MenuItem}
      initialValue={colDef.field}
      validator={validator}
      onClick={onClick}
      onCancel={hideMenu}
      endIcon={<AutoFixHighSharpIcon sx={{ scale: "0.8" }} />}
      title={"Modify year"}
      confirmationButtonLabel={"Modify"}
    >
      <ListItemIcon>
        <AutoFixHighSharpIcon sx={{ scale: "0.8" }} />
      </ListItemIcon>
      <ListItemText>Modify</ListItemText>
    </WithValueModalComponent>
  );
};
const CustomModifyCurrencyItem = ({ value, onEvent, hideMenu }) => {
  const { currencies } = useContext(ConstantContext);

  const onClick = (currency_code) => {
    onEvent("modifyCurrency", { currency_code: currency_code });
    hideMenu();
  };

  const validator = (currency_code) => {
    var result = Object.values(currencies).find(
      (x) => x.code === currency_code,
    );
    if (!result)
      throw Error(
        `The currency code '${currency_code}' is not valid. ` +
          "Please refer to this list : https://fr.wikipedia.org/wiki/ISO_4217#Index_par_code",
      );
  };

  return (
    <WithValueModalComponent
      Component={MenuItem}
      initialValue={
        Object.values(currencies).find((x) => x.code === value.currency_code)
          .code
      }
      validator={validator}
      onClick={onClick}
      onCancel={hideMenu}
      endIcon={<AutoFixHighSharpIcon sx={{ scale: "0.8" }} />}
      title={"Modify currency"}
      confirmationButtonLabel={"Modify"}
    >
      <ListItemIcon>
        <AutoFixHighSharpIcon sx={{ scale: "0.8" }} />
      </ListItemIcon>
      <ListItemText>Modify</ListItemText>
    </WithValueModalComponent>
  );
};
const CustomColumnMenu = (props) => {
  return props.colDef.field === "Label" ? (
    <Stack>
      <CustomModifyCurrencyItem {...props} />
    </Stack>
  ) : (
    <Stack>
      <CustomAddColumnItem {...props} />
      <CustomModifyColumnItem {...props} />
      <CustomDeleteColumnItem {...props} />
    </Stack>
  );
};
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .derivated-row": {
    backgroundColor: theme.palette.secondary.extralight,
    fontStyle: "italic",
    fontSize: "90%",
    "&: hover": {
      backgroundColor: theme.palette.secondary.extralight,
    },
  },
  "&.MuiDataGrid-root": {
    height: "unset",
  },
  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
  },
  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
    outline: "none !important",
  },
  "input::-webkit-outer-spin-button,input::-webkit-inner-spin-button ": {
    WebkitAppearance: "none",
    margin: 0,
  },
}));
const valueFormatter = (value, column) => {
  const valueFloat = parseFloat(value);
  if (isNaN(valueFloat)) return "";

  if (
    ["growthRate", "percentEbitda", "percentGrossProfit"].includes(column.id)
  ) {
    return `${valueFloat.toFixed(1)} %`;
  }
  return valueFloat.toFixed(1);
};
const getDefaultYears = () => {
  var today = new Date();
  return [
    today.getFullYear() - 1,
    today.getFullYear(),
    today.getFullYear() + 1,
  ];
};
const getDefaultValue = () => {
  return {
    currency_code: "EUR",
    years: getDefaultYears().map((year) => {
      return { year: year };
    }),
  };
};
const FinancialsDataGrid = forwardRef(
  ({ label, onChange, value, disabled, isDirty }, ref) => {
    const { currencies } = useContext(ConstantContext);
    const { displayError } = useContext(SnackbarContext);
    const [state, setState] = useState();
    const [innerValue, setInnerValue] = useState();

    const onEvent = (event, params) => {
      switch (event) {
        case "addColumn":
          innerValue.years.push({ year: parseInt(params.newYear) });
          onChange({ ...innerValue });
          break;
        case "modifyColumn":
          const oldYearIndex = innerValue.years.findIndex(
            (x) => x.year == params.oldYear,
          );
          const newYearObject = innerValue.years[oldYearIndex];
          newYearObject.year = parseInt(params.newYear);
          innerValue.years[params.oldYearIndex] = newYearObject;
          onChange({ ...innerValue });
          break;
        case "deleteColumn":
          const newYears = innerValue.years.filter(
            (x) => x.year != params.year,
          );
          innerValue.years = newYears;
          onChange({ ...innerValue });
          break;
        case "modifyCurrency":
          innerValue.currency_code = params.currency_code;
          onChange({ ...innerValue });
          break;
        default:
          throw new Error(`Unknown event '${event}'`);
      }
    };

    useEffect(() => {
      setInnerValue(value || getDefaultValue());
    }, [value]);

    useEffect(() => {
      var validation_response = validator.validate(
        innerValue || null,
        validationSchema,
      );
      if (!validation_response.valid) {
        setState({ ...state, validation_response: validation_response });
        return;
      }

      const first_column_with_currency_code = JSON.parse(
        JSON.stringify(firstColumn),
      );
      first_column_with_currency_code.headerName = innerValue?.currency_code
        ? `${innerValue.currency_code || ""} (${
            Object.values(currencies).find(
              (x) => x.code === innerValue.currency_code,
            )?.symbol || ""
          })`
        : "";
      const columns_by_year = Object.fromEntries(
        innerValue.years.map((year) => [
          year.year,
          {
            field: year.year.toString(),
            headerName: year.year.toString(),
            editable: !disabled,
            type: "number",
            disableColumnMenu: disabled,
            sortable: false,
            valueFormatter: valueFormatter,
          },
        ]),
      );
      const rows = JSON.parse(JSON.stringify(rowsDefinitions));
      // fill the rows
      innerValue.years.forEach((year) => {
        rows.forEach((row) => {
          row[columns_by_year[year.year].field] = year[row.id];
        });
      });

      setState(
        recalcCalculatedValues({
          columns: [first_column_with_currency_code].concat(
            Object.keys(columns_by_year).map((year) => columns_by_year[year]),
          ),
          rows: rows,
          validation_response: validation_response,
        }),
      );
    }, [innerValue]);

    const processRowUpdate = (newRow, oldRow) => {
      if (JSON.stringify(newRow) == JSON.stringify(oldRow)) return newRow;
      const newValue = JSON.parse(JSON.stringify(innerValue));
      Object.keys(newRow).forEach((year) => {
        if (["Label", "id", "props"].includes(year)) return;
        if (newValue.years.find((x) => x.year.toString() == year) == undefined)
          return;
        if (isNaN(parseFloat(newRow[year]))) {
          newValue.years.find((x) => x.year.toString() == year)[newRow.id] =
            null;
        } else {
          newValue.years.find((x) => x.year.toString() == year)[newRow.id] =
            parseFloat(newRow[year]);
        }
      });
      onChange(newValue);
      return newRow;
    };

    if (!state)
      return <Skeleton variant="rectangular" width="100%" height={200} />;
    if (!state.validation_response.valid) {
      return (
        <Stack direction="row" alignItems="center">
          <Tooltip
            title={
              <pre>
                {JSON.stringify(state.validation_response.errors, null, 4)}
              </pre>
            }
          >
            <InfoIcon
              sx={{ mr: 0.5, scale: "0.7", opacity: "0.7" }}
              color="error"
            />
          </Tooltip>
          <Typography variant="subtitle2" sx={{ color: "error.main" }}>
            The financials data has not a valid format.
          </Typography>
        </Stack>
      );
    } else {
      return (
        <>
          {label && <Typography variant="subtitle2">{label}</Typography>}
          <div>
            <StyledDataGrid
              ref={ref}
              getRowId={(row) => row.id}
              columns={state.columns}
              rows={state.rows}
              disableRowSelectionOnClick
              getRowHeight={getRowHeight}
              slots={{
                columnMenu: CustomColumnMenu,
              }}
              slotProps={{
                columnMenu: {
                  value: innerValue,
                  onEvent,
                },
              }}
              disableColumnMenu={disabled}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(error) => {
                displayError(`[ERROR] : ${error}`);
              }}
              isCellEditable={isCellEditable}
              density="compact"
              getRowClassName={getRowClassName}
              hideFooter
            />
          </div>
          {isDirty && (
            <Stack direction="row" alignItems="center">
              <InfoIcon
                sx={{ mr: 0.5, scale: "0.7", opacity: "0.7" }}
                color="secondary"
              />
              <Typography variant="caption" sx={{ color: "secondary.main" }}>
                Financials has been modified
              </Typography>
            </Stack>
          )}
        </>
      );
    }
  },
);

export default FinancialsDataGrid;
